// Extracted from original/src/App.tsx:46987-47290 (`{showProfitBreakdown && (...)}`, "تفاصيل الأرباح"), opened by the
// dashboard profit card (`handleShowProfitBreakdown`, :17979-17988).
// Kept: the backdrop and dialog frame, the header, the five sections (by box, operating profit by operator, expenses
// and salaries by employee, net profit by employee, other-currency profits) with their class strings, colours and
// the empty message; "+X $" for dollar figures and "X د.ع" for dinar figures.
// Cut: `dashboardData.walletProfits / operatorProfits / operatorExpenses / currencyProfits` and the name-suffix test
// for dollar rows (`name.includes("($)")`, :47027-47031) → rows from the server (`GET /dashboard` profit.breakdown,
// the since-settlement period of the one profit engine, D-104) with an explicit `usd` flag and preformatted figures;
// the net per employee is the server's (old: browser subtraction of all-time expenses from since-settlement profit);
// the other-currency "تحويل" button (:47241-47265; D-101: only USD profit is converted, from the Profits screen) — the
// section renders only for currencies other than IQD / USD, which hold no profit in the new ledger (D-067).
// No money arithmetic: every figure is a string formatted by the container.
import { motion } from 'motion/react';
import { Coins, DollarSign, TrendingUp, X } from 'lucide-react';
import { useI18n } from '../i18n-context.js';

export interface BreakdownAmountRow {
  readonly name: string;
  /** Formatted absolute figure ("20,000"); sign carried by `negative`. */
  readonly amount: string;
  readonly usd: boolean;
}

export interface BreakdownNetRow {
  readonly name: string;
  readonly usd: boolean;
  /** Formatted with its sign handled by the container ("+20,000" / "-230,000"). */
  readonly net: string;
  readonly positive: boolean;
  readonly gross: string;
  readonly expenses: string;
}

export interface BreakdownCurrencyRow {
  readonly currency: string;
  readonly pending: string;
  readonly gross: string;
  readonly expenses: string;
  readonly settled: string;
}

export interface ProfitBreakdownModalProps {
  readonly wallets: readonly BreakdownAmountRow[];
  readonly operators: readonly BreakdownAmountRow[];
  readonly expenses: readonly BreakdownAmountRow[];
  readonly net: readonly BreakdownNetRow[];
  readonly otherCurrencies: readonly BreakdownCurrencyRow[];
  readonly onClose: () => void;
}

export function ProfitBreakdownModal(p: ProfitBreakdownModalProps) {
  const { t } = useI18n();
  const unit = t('unit.iqd');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="profit-breakdown-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={p.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-lg rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-emerald-50 dark:bg-emerald-900/10">
          <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] dark:digital-glow uppercase tracking-tight">
            <TrendingUp className="text-emerald-600 dark:text-emerald-400" />
            {t('pb.title')}
          </h3>
          <button
            onClick={p.onClose}
            data-testid="profit-breakdown-close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {/* By Wallet */}
          <div>
            <h4 className="text-sm font-black text-gray-400 dark:text-[#8b949e] mb-4 uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d] pb-2">
              {t('pb.by_wallet')}
            </h4>
            <div className="space-y-3" data-testid="pb-wallets">
              {p.wallets.map((wp, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d]"
                >
                  <span className="font-bold dark:text-[#f0f6fc]">
                    {wp.name}
                  </span>
                  <span className="font-mono font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    {wp.usd
                      ? `+${wp.amount}`
                      : `${wp.amount} ${unit}`}
                    {wp.usd && (
                      <DollarSign
                        size={14}
                        className="text-emerald-600 dark:text-emerald-400"
                      />
                    )}
                  </span>
                </div>
              ))}
              {p.wallets.length === 0 && (
                <p className="text-center text-gray-400 py-4 italic text-sm">
                  {t('pb.no_profits')}
                </p>
              )}
            </div>
          </div>
          {/* By Operator */}
          <div>
            <h4 className="text-sm font-black text-gray-400 dark:text-[#8b949e] mb-4 uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d] pb-2">
              {t('pb.by_operator')}
            </h4>
            <div className="space-y-3" data-testid="pb-operators">
              {p.operators.map((op, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d]"
                >
                  <span className="font-bold dark:text-[#f0f6fc]">
                    {op.name}
                  </span>
                  <span className="font-mono font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    {op.amount}
                    {op.usd ? (
                      <DollarSign
                        size={14}
                        className="text-emerald-600 dark:text-emerald-400"
                      />
                    ) : (
                      ` ${unit}`
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* By Operator Expenses */}
          <div>
            <h4 className="text-sm font-black text-rose-400 dark:text-rose-500 mb-4 uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d] pb-2">
              {t('pb.expenses_by_operator')}
            </h4>
            <div className="space-y-3" data-testid="pb-expenses">
              {p.expenses.map((ex, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d]"
                >
                  <span className="font-bold dark:text-[#f0f6fc]">
                    {ex.name}
                  </span>
                  <span className="font-mono font-black text-rose-600 dark:text-rose-400">
                    {ex.amount} {unit}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* Net Profit By Operator */}
          <div>
            <h4 className="text-sm font-black text-blue-400 dark:text-blue-500 mb-4 uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d] pb-2">
              {t('pb.net_by_operator')}
            </h4>
            <div className="space-y-3" data-testid="pb-net">
              {p.net.map((op, i) => (
                <div
                  key={i}
                  className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold dark:text-[#f0f6fc]">
                      {op.name}
                    </span>
                    <span
                      className={`font-mono font-black ${op.positive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"} flex items-center gap-1`}
                    >
                      {op.net}
                      {op.usd ? <DollarSign size={14} /> : ` ${unit}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px] font-bold text-gray-400">
                    <span>
                      {t('pb.gross')}{" "}
                      {op.gross}
                      {op.usd ? " $" : ` ${unit}`}
                    </span>
                    {!op.usd && (
                      <span>
                        {t('pb.expenses')}{" "}
                        {op.expenses} {unit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Other Currency Profits */}
          {p.otherCurrencies.length > 0 && (
            <div>
              <h4 className="text-sm font-black text-emerald-600 dark:text-emerald-400 mb-4 uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d] pb-2 flex items-center justify-between">
                <span>
                  {t('pb.other_currencies')}
                </span>
                <Coins size={16} />
              </h4>
              <div className="space-y-3">
                {p.otherCurrencies.map((c, cIdx) => (
                  <div
                    key={`profit-breakdown-${c.currency || cIdx}`}
                    className="p-3 bg-emerald-50/50 dark:bg-emerald-950/10 rounded-xl border border-emerald-100 dark:border-emerald-900/30 space-y-1"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 dark:text-[#f0f6fc]">
                        {t('pb.currency_profits', { cur: c.currency })}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-black text-emerald-600 dark:text-emerald-400">
                          +{c.pending}{" "}
                          {c.currency}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 font-bold">
                      <span>
                        {t('pb.total')}{" "}
                        {c.gross} {c.currency}
                      </span>
                      <span>
                        {t('pb.expenses')}{" "}
                        {c.expenses} {c.currency}
                      </span>
                      <span>
                        {t('pb.settled')}{" "}
                        {c.settled}{" "}
                        {c.currency}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
